/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { css } from "@emotion/css";
import { NumberField, RadioButtonNew, RadioGroupNew, TextField, InlineMessage, PopoverBasicHelp } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { Repository, SigningKeyConfigurationResource, AuthenticationConfigurationResource, PublicKeyHostingLocation } from "@octopusdeploy/octopus-server-client";
import { useEffect, useState } from "react";
import { getAuthenticationConfiguration } from "~/areas/configuration/components/SigningKeys/getAuthenticationConfiguration";
import { Form, useForm } from "~/components/form/Form/Form";
import { WorkflowContent } from "~/workflows/WorkflowContent";
import { workflows } from "~/workflows/workflows";
export async function editSigningKeyConfigurationWorkflowLoader(repository: Repository): Promise<EditSigningKeyConfigurationWorkflowLoaderData> {
    const configuration = await repository.SigningKeyConfiguration.get();
    const { authenticationConfiguration } = await getAuthenticationConfiguration(repository);
    return { configuration, authenticationConfiguration };
}
interface EditSigningKeyConfigurationWorkflowLoaderData {
    configuration: SigningKeyConfigurationResource;
    authenticationConfiguration: AuthenticationConfigurationResource;
}
interface EditSigningKeyConfigurationWorkflowProps {
    loaderData: EditSigningKeyConfigurationWorkflowLoaderData | null;
    completeWorkflow: (config: SigningKeyConfigurationResource) => void;
}
type EditSigningKeyConfigurationModel = {
    publicKeyHostingLocation: PublicKeyHostingLocation;
    revokeAfterDays: number;
    expireAfterDays: number;
    oidcIssuerUrl: string | undefined;
};
type EditSigningKeyConfigurationOutput = {
    configuration: SigningKeyConfigurationResource;
    authenticationConfiguration: AuthenticationConfigurationResource;
    nextStep: "validate" | "complete";
};
type DialogStep = "initial" | "validate";
export function EditSigningKeyConfigurationWorkflow({ loaderData, completeWorkflow }: EditSigningKeyConfigurationWorkflowProps) {
    const [currentStep, setCurrentStep] = useState<DialogStep>("initial");
    const { model, setModel, formProps, createSaveAction, getFieldError } = useForm<EditSigningKeyConfigurationModel, EditSigningKeyConfigurationOutput>({
        formName: "Edit Signing Key Configuration",
        initialModel: {
            expireAfterDays: 90,
            publicKeyHostingLocation: "Internal",
            revokeAfterDays: 90,
            oidcIssuerUrl: undefined,
        },
        onSubmit: async (repository, model) => {
            if (currentStep === "initial") {
                const { authenticationConfiguration } = await getAuthenticationConfiguration(repository);
                const configuration = await repository.SigningKeyConfiguration.get();
                configuration.PublicKeyHostingLocation = model.publicKeyHostingLocation;
                if (model.publicKeyHostingLocation === "External") {
                    authenticationConfiguration.OidcConfigurationSettings.OidcIssuerUrl = model.oidcIssuerUrl ?? null;
                    return {
                        configuration: configuration,
                        authenticationConfiguration,
                        nextStep: "validate",
                    };
                }
                configuration.ExpireAfterDays = model.expireAfterDays!;
                configuration.RevokeAfterDays = model.revokeAfterDays!;
                const updatedConfiguration = await repository.SigningKeyConfiguration.modify(configuration);
                return {
                    configuration: updatedConfiguration,
                    authenticationConfiguration,
                    nextStep: "complete",
                };
            }
            else {
                const configuration = await repository.SigningKeyConfiguration.get();
                const { authenticationConfiguration, authenticationMetaData } = await getAuthenticationConfiguration(repository);
                configuration.PublicKeyHostingLocation = model.publicKeyHostingLocation;
                authenticationConfiguration.OidcConfigurationSettings.OidcIssuerUrl = model.oidcIssuerUrl ?? null;
                const updatedConfiguration = await repository.SigningKeyConfiguration.modify(configuration);
                await repository.Settings.saveValues(authenticationMetaData, authenticationConfiguration);
                return {
                    configuration: updatedConfiguration,
                    authenticationConfiguration,
                    nextStep: "complete",
                };
            }
        },
        updateFormModelAfterSubmit: (output) => {
            return {
                publicKeyHostingLocation: output.configuration.PublicKeyHostingLocation,
                expireAfterDays: output.configuration.ExpireAfterDays,
                revokeAfterDays: output.configuration.RevokeAfterDays,
                oidcIssuerUrl: output.authenticationConfiguration.OidcConfigurationSettings?.OidcIssuerUrl ?? undefined,
            };
        },
        afterSubmit: async (repository, output) => {
            if (output.nextStep === "validate") {
                // Trigger the download of signing keys
                window.location.href = repository.SigningKeys.getExportLink(model.oidcIssuerUrl);
                setCurrentStep("validate");
            }
            else {
                completeWorkflow(output.configuration);
            }
        },
        validate: (model) => {
            if (model.publicKeyHostingLocation === "External") {
                if (!model.oidcIssuerUrl) {
                    return {
                        errorMessage: "OIDC Issuer URL is required when hosting public keys externally",
                        fieldErrors: { oidcIssuerUrl: "Please enter an OIDC Issuer URL" },
                    };
                }
                try {
                    const parsedUrl = new URL(model.oidcIssuerUrl!);
                    if (!parsedUrl.protocol.startsWith("https:")) {
                        return {
                            errorMessage: "OIDC Issuer URL must use HTTPS",
                            fieldErrors: { oidcIssuerUrl: "OIDC Issuer URL must use HTTPS" },
                        };
                    }
                }
                catch (e) {
                    return {
                        errorMessage: "Please enter a valid OIDC Issuer URL",
                        fieldErrors: { oidcIssuerUrl: "Please enter a valid OIDC Issuer URL" },
                    };
                }
            }
            return undefined;
        },
        snackbarSaveText: "Signing key configuration updated",
    });
    useEffect(() => {
        if (loaderData) {
            setModel({
                publicKeyHostingLocation: loaderData.configuration.PublicKeyHostingLocation,
                expireAfterDays: loaderData.configuration.ExpireAfterDays,
                revokeAfterDays: loaderData.configuration.RevokeAfterDays,
                oidcIssuerUrl: loaderData.authenticationConfiguration.OidcConfigurationSettings.OidcIssuerUrl ?? undefined,
            });
        }
    }, [loaderData, setModel]);
    const getDialogTitle = () => {
        if (currentStep === "validate")
            return "Validate Signing Keys";
        return "Edit Signing Key Configuration";
    };
    const getButtonLabel = () => {
        if (currentStep === "validate")
            return "Mark as validated";
        if (model.publicKeyHostingLocation === "External")
            return "Export Signing Keys";
        return "Save";
    };
    const indentedControls = css({
        marginTop: space[8],
        marginLeft: "1.75rem",
        marginBottom: space[4],
        display: "flex",
        flexDirection: "column",
        gap: space[4],
    });
    return (<WorkflowContent.Dialog workflow={workflows.editSigningKeyConfigurationWorkflow} title={getDialogTitle()} primaryAction={createSaveAction({ saveButtonLabel: getButtonLabel(), enabledWhenClean: currentStep === "validate" })}>
            <Form {...formProps}>
                {currentStep === "initial" && loaderData ? (<RadioGroupNew value={model.publicKeyHostingLocation} onChange={(value) => setModel((prev) => ({ ...prev, publicKeyHostingLocation: value }))} label="Signing Key Configuration" description="" hideDescription={true} popover={<PopoverBasicHelp placement="right-start" description={<div>
                                        When hosting public keys with Octopus, we automatically rotate your signing keys every 90 days by default to maintain security. With externally hosted keys, you'll need to manually export and rotate keys
                                        yourself.
                                    </div>}/>}>
                        <RadioButtonNew value="Internal" label="Internally hosted public keys" description="Automatically rotate signing keys" name="signingKeyHosting"/>

                        {model.publicKeyHostingLocation === "Internal" && (<div className={indentedControls}>
                                <NumberField value={model.expireAfterDays} label="Rotate after days" onChange={(val: number | undefined) => {
                    if (val === undefined || val <= 0) {
                        val = 0;
                    }
                    setModel((prev) => ({ ...prev, expireAfterDays: val! }));
                }} description={`The active key will be automatically rotated ${model.expireAfterDays} days after it's created`} suffix="days"/>
                                <NumberField value={model.revokeAfterDays} label="Revoke after days" onChange={(val: number | undefined) => {
                    if (val === undefined || val <= 0) {
                        val = 0;
                    }
                    setModel((prev) => ({ ...prev, revokeAfterDays: val! }));
                }} description={`Expired signing keys will be automatically revoked ${model.revokeAfterDays} days after they expire`} suffix="days"/>
                            </div>)}

                        <RadioButtonNew value="External" label="Externally hosted public keys" description="Manually rotate signing keys" name="signingKeyHosting"/>

                        {model.publicKeyHostingLocation === "External" && (<div className={indentedControls}>
                                <TextField label="OIDC Issuer URL" value={model.oidcIssuerUrl ?? ""} onChange={(value) => {
                    setModel((prev) => ({ ...prev, oidcIssuerUrl: value || undefined }));
                }} error={getFieldError("oidcIssuerUrl")} popover={<PopoverBasicHelp placement="right-start" description={<div>We fetch your public keys from this URL to verify requests come from your Octopus instance. Make sure this URL is publicly accessible via HTTPS.</div>}/>}/>

                                <InlineMessage type="information" description="Externally hosting public keys means you'll manage signing key rotation manually. To use keys in network-restricted environments, you'll need to export them."/>
                            </div>)}
                    </RadioGroupNew>) : null}
                {currentStep === "validate" && (<div>
                        <p>Once you've uploaded your exported signing keys, confirm the configuration is working.</p>
                    </div>)}
            </Form>
        </WorkflowContent.Dialog>);
}
